import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/auth/hooks/useAuth';

interface ProtectedAdminRouteProps {
    children: React.ReactNode;
}

/**
 * Protected Route wrapper for Admin pages
 * Ensures only authenticated admin users can access admin routes
 */
const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F1F3F4] flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6] mb-4"></div>
                    <p className="text-base font-medium text-[#757575]">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
    }

    // Check if user is admin
    const userRole = user?.userType;
    const isAdmin = userRole === 'hush_admin';

    // Redirect to appropriate dashboard if not admin
    if (!isAdmin) {
        // Redirect based on user role
        const redirectPath = userRole === 'company' || userRole === 'recruiter'
            ? '/companies/dashboard'
            : '/candidate/dashboard';

        return <Navigate to={redirectPath} replace />;
    }

    // User is authenticated and is an admin
    return <>{children}</>;
};

export default ProtectedAdminRoute;
